/**
 * Save slots (spec §10, §11): 3 manual slots and 3 rolling autosaves over a `SaveStorage`.
 *
 * - Every read is parsed, migrated and validated; a bad slot is reported (`corrupt` or `future`, with the error)
 *   and is **never overwritten** by an autosave: the rolling `auto1 → auto2 → auto3` skips it.
 * - A manual save refuses to overwrite a bad slot unless `overwriteBad` is set (the UI confirms first).
 * - Loads never rewrite the stored text, so a failed migration leaves the original to be exported as-is.
 * - The first successful write asks for persistent storage (`persist`).
 */
import { fail, ok, type CmdFailure, type CmdResult } from '../career/result'
import { logger } from '../core/logger'
import { AUTO_SLOTS, MANUAL_SLOTS, type AutoSlotId, type ManualSlotId, type SaveSlotId, type SaveSlotInfo } from '../ui/contracts'
import { migrate, type MigrateOptions } from './migrations'
import { checkRecordSize, serializeRecord, type SaveRecord } from './record'
import { requestPersistence } from './persist'
import type { SaveStorage } from './storage'

export interface SaveSlotsOptions {
  storage: SaveStorage
  /** `career.json save.maxBytes`: larger records still save, with a warning. */
  maxBytes: number
  /** `career.json save.manualSlots` / `autoSlots` (default 3 each). */
  manualSlots?: number
  autoSlots?: number
  /** Default: `requestPersistence` (navigator.storage.persist, once). */
  persist?: () => Promise<boolean>
  /** Migration options (tests inject a chain). */
  migrate?: MigrateOptions
}

/** A slot read back: its info, and the migrated record when it is valid. */
export interface SlotRead {
  info: SaveSlotInfo
  record: SaveRecord | null
  /** The stored text (null when empty). */
  text: string | null
}

export interface SaveManualOptions {
  /** Overwrite a slot that holds a corrupt or future save (after a UI confirm). */
  overwriteBad?: boolean
}

export interface SaveSlots {
  readonly storage: SaveStorage
  /** Manual then auto slots, as configured. */
  readonly slots: readonly SaveSlotId[]
  saveManual(slot: ManualSlotId, record: SaveRecord, options?: SaveManualOptions): Promise<CmdResult<SaveSlotInfo>>
  /** Rolls the valid autosaves down one slot and writes `record` to the first writable one. */
  autosave(record: SaveRecord): Promise<CmdResult<AutoSlotId>>
  read(slot: SaveSlotId): Promise<SlotRead>
  listSlots(): Promise<SaveSlotInfo[]>
  /** The newest valid save by `savedAt` (then game time), or null. */
  newestValid(): Promise<{ slot: SaveSlotId; record: SaveRecord } | null>
  load(slot: SaveSlotId): Promise<CmdResult<SaveRecord>>
  /** The stored text as it is (export of a corrupt or unmigratable save), or null when empty. */
  rawText(slot: SaveSlotId): Promise<string | null>
}

/** "Slot 2", "Autosave 1". */
export function slotLabel(slot: SaveSlotId): string {
  return slot.startsWith('auto') ? `Autosave ${slot.slice(4)}` : `Slot ${slot.slice(4)}`
}

function infoFromRecord(slot: SaveSlotId, record: SaveRecord): SaveSlotInfo {
  return {
    slot,
    status: 'ok',
    savedAt: record.savedAt,
    gameTime: record.gameTime,
    wallet: record.career.wallet,
    principal: record.career.lease.principal,
    station: record.career.lastDockedStation,
  }
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

function isBad(info: SaveSlotInfo): boolean {
  return info.status === 'corrupt' || info.status === 'future'
}

export function createSaveSlots(options: SaveSlotsOptions): SaveSlots {
  const { storage, maxBytes } = options
  const manual = MANUAL_SLOTS.slice(0, options.manualSlots ?? MANUAL_SLOTS.length)
  const auto = AUTO_SLOTS.slice(0, options.autoSlots ?? AUTO_SLOTS.length)
  const slots: readonly SaveSlotId[] = [...manual, ...auto]
  const persist = options.persist ?? requestPersistence

  type Decoded = { ok: true; info: SaveSlotInfo; record: SaveRecord } | { ok: false; info: SaveSlotInfo; error: CmdFailure }

  function decode(slot: SaveSlotId, text: string): Decoded {
    let raw: unknown
    try {
      raw = JSON.parse(text)
    } catch (err) {
      const message = `Not valid JSON: ${errorText(err)}`
      return { ok: false, info: { slot, status: 'corrupt', error: message }, error: fail('corrupt', `${slotLabel(slot)} is corrupt. ${message}`, { slot }) }
    }
    const result = migrate(raw, options.migrate)
    if (result.ok) return { ok: true, info: infoFromRecord(slot, result.value.record), record: result.value.record }
    const { code, message, detail } = result.error
    return {
      ok: false,
      info: { slot, status: code === 'future' ? 'future' : 'corrupt', error: message },
      error: fail(code, `${slotLabel(slot)}: ${message}`, { ...detail, slot }),
    }
  }

  /** The stored text, or a storage error (reported as a bad slot, so nothing overwrites it). */
  async function getText(slot: SaveSlotId): Promise<{ text: string | null; error: CmdFailure | null }> {
    try {
      return { text: await storage.get(slot), error: null }
    } catch (err) {
      logger.error(`save: reading ${slot} failed`, err)
      return { text: null, error: fail('storageError', `Could not read ${slotLabel(slot)}: ${errorText(err)}`, { slot }) }
    }
  }

  async function read(slot: SaveSlotId): Promise<SlotRead> {
    const got = await getText(slot)
    if (got.error !== null) return { info: { slot, status: 'corrupt', error: got.error.error.message }, record: null, text: null }
    if (got.text === null) return { info: { slot, status: 'empty' }, record: null, text: null }
    const decoded = decode(slot, got.text)
    return { info: decoded.info, record: decoded.ok ? decoded.record : null, text: got.text }
  }

  async function write(slot: SaveSlotId, text: string): Promise<CmdResult<null>> {
    try {
      await storage.put(slot, text)
    } catch (err) {
      logger.error(`save: writing ${slot} failed`, err)
      return fail('storageError', `Could not save to ${slotLabel(slot)}: ${errorText(err)}`, { slot })
    }
    void persist()
    return ok(null)
  }

  /** Writes run one at a time, so an autosave never interleaves with a manual save or another autosave. */
  let queue: Promise<unknown> = Promise.resolve()
  function serial<T>(task: () => Promise<T>): Promise<T> {
    const run = queue.then(task, task)
    queue = run.catch(() => undefined)
    return run
  }

  function encode(record: SaveRecord): string {
    checkRecordSize(record, maxBytes)
    return serializeRecord(record)
  }

  return {
    storage,
    slots,
    read,

    saveManual: (slot, record, saveOptions = {}) => serial(async (): Promise<CmdResult<SaveSlotInfo>> => {
      if (!manual.includes(slot)) return fail('storageError', `${slotLabel(slot)} is not a manual save slot here.`, { slot })
      if (saveOptions.overwriteBad !== true) {
        const existing = await read(slot)
        if (isBad(existing.info)) {
          return fail('corrupt', `${slotLabel(slot)} holds an unreadable save (${existing.info.error ?? 'unknown error'}). Export it or confirm to overwrite.`, {
            slot,
          })
        }
      }
      const written = await write(slot, encode(record))
      return written.ok ? ok(infoFromRecord(slot, record)) : written
    }),

    autosave: (record) => serial(async (): Promise<CmdResult<AutoSlotId>> => {
      const reads = await Promise.all(auto.map((slot) => read(slot)))
      const writable = auto.filter((_, i) => !isBad(reads[i].info))
      if (writable.length === 0) {
        return fail('storageError', 'Autosave skipped: every autosave slot holds an unreadable save.')
      }
      // The valid texts move down one writable slot (raw text, never re-serialised); the oldest falls off.
      const carried: string[] = []
      for (const r of reads) if (!isBad(r.info) && r.text !== null) carried.push(r.text)
      const next = [encode(record), ...carried].slice(0, writable.length)
      for (let i = next.length - 1; i >= 0; i--) {
        const written = await write(writable[i], next[i])
        if (!written.ok) return written
      }
      return ok(writable[0])
    }),

    async listSlots() {
      const reads = await Promise.all(slots.map((slot) => read(slot)))
      return reads.map((r) => r.info)
    },

    async newestValid() {
      let best: { slot: SaveSlotId; record: SaveRecord } | null = null
      for (const slot of slots) {
        const { record } = await read(slot)
        if (record === null) continue
        if (
          best === null ||
          record.savedAt > best.record.savedAt ||
          (record.savedAt === best.record.savedAt && record.gameTime > best.record.gameTime)
        ) {
          best = { slot, record }
        }
      }
      return best
    },

    async load(slot) {
      const got = await getText(slot)
      if (got.error !== null) return got.error
      if (got.text === null) return fail('emptySlot', `${slotLabel(slot)} is empty.`, { slot })
      const decoded = decode(slot, got.text)
      return decoded.ok ? ok(decoded.record) : decoded.error
    },

    rawText: (slot) => storage.get(slot),
  }
}
